"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Avatar } from "./primitives.tsx";
import { CheckIcon, ChevronIcon, SearchIcon } from "./icons.tsx";
import { Popover, usePopover } from "./popover.tsx";
import { useListCursor, useScrollIntoView, type Placement } from "./hooks.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Searchable menus.

   The bar to clear is the creator dropdown in numero-creators: you can point
   at a face, or type past fifty of them, and it is instant either way because
   the whole list is already in memory and the filter is local.

   Everything here is one component with the pieces made optional, so a plain
   status menu and the creator picker behave identically under the keyboard.
   Nothing on this page is ever a browser `<select>`.
   ────────────────────────────────────────────────────────────────────────── */

export type Option<T extends string = string> = {
  value: T;
  label: string;
  hint?: string;
  /** Rendered as an avatar; falls back to initials of `label`. */
  image?: string | null;
  /** A colour dot, for categories and tags. */
  colour?: string;
  icon?: ReactNode;
  badge?: ReactNode;
  disabled?: boolean;
  group?: string;
  /** Extra text the search should match but the row should not show. */
  keywords?: string;
};

/**
 * Prefix matches first.
 *
 * Typing "mi" should offer MIA and MIO before MIRIAM, and neither before
 * something that only matches in a keyword. Ties keep the incoming order,
 * because Array#sort is stable and the caller already sorted sensibly.
 */
export function filterOptions<T extends string>(options: Option<T>[], query: string): Option<T>[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return options;
  const scored: { option: Option<T>; score: number }[] = [];
  for (const option of options) {
    const label = option.label.toLowerCase();
    const hint = (option.hint ?? "").toLowerCase();
    const keywords = (option.keywords ?? "").toLowerCase();
    if (label.startsWith(needle)) scored.push({ option, score: 0 });
    else if (label.includes(needle)) scored.push({ option, score: 1 });
    else if (hint.includes(needle)) scored.push({ option, score: 2 });
    else if (keywords.includes(needle)) scored.push({ option, score: 3 });
  }
  return scored.sort((a, b) => a.score - b.score).map((entry) => entry.option);
}

function OptionRow<T extends string>({
  option,
  selected,
  cursor,
  multi,
  onHover,
  onPick,
}: {
  option: Option<T>;
  selected: boolean;
  cursor: boolean;
  multi: boolean;
  onHover: () => void;
  onPick: () => void;
}) {
  const hasVisual = option.image !== undefined || option.colour || option.icon;
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={option.disabled}
      className={[selected ? "active" : "", cursor ? "cursor" : ""].filter(Boolean).join(" ")}
      onPointerEnter={onHover}
      onClick={onPick}
    >
      <span style={{ display: "grid", placeItems: "center", width: hasVisual ? 26 : 0 }}>
        {option.image !== undefined ? (
          <Avatar name={option.label} src={option.image} size="small" square />
        ) : option.colour ? (
          <i style={{ width: 9, height: 9, borderRadius: 3, background: option.colour, display: "block" }} />
        ) : (
          option.icon
        )}
      </span>
      <span className="pop-option-copy">
        <strong>
          <span>{option.label}</span>
          {option.badge}
        </strong>
        {option.hint ? <small>{option.hint}</small> : null}
      </span>
      <span className="check" style={{ display: "grid", placeItems: "center" }}>
        {multi ? (
          <span className={`checkbox${selected ? " on" : ""}`} style={{ width: 15, height: 15, pointerEvents: "none" }}>
            <CheckIcon size={10} strokeWidth={3} />
          </span>
        ) : selected ? (
          <CheckIcon size={13} />
        ) : null}
      </span>
    </button>
  );
}

function OptionList<T extends string>({
  options,
  selected,
  multi,
  cursor,
  setCursor,
  onPick,
  emptyLabel,
  emptyAction,
  onKeyDown,
  autoFocus = false,
}: {
  options: Option<T>[];
  selected: Set<string>;
  multi: boolean;
  cursor: number;
  setCursor: (index: number) => void;
  onPick: (value: T) => void;
  emptyLabel: string;
  /** Offered when nothing matched: usually "make one called that". */
  emptyAction?: ReactNode;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  /** Set when there is no search box, so the arrows work immediately. */
  autoFocus?: boolean;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  useScrollIntoView(listRef, cursor, true);

  useEffect(() => {
    if (autoFocus) listRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  if (options.length === 0) {
    return (
      <div className="pop-options">
        <div className="panel-empty" style={{ padding: emptyAction ? "18px 16px 14px" : "22px 16px" }}>
          {emptyLabel}
          {emptyAction ? <div style={{ marginTop: 10 }}>{emptyAction}</div> : null}
        </div>
      </div>
    );
  }

  // Grouping is flattened into the same list so the cursor index and the
  // rendered order cannot disagree — a separate grouped render is where
  // arrow-key navigation quietly stops matching what is on screen.
  const rows: ReactNode[] = [];
  let lastGroup: string | undefined;
  options.forEach((option, index) => {
    if (option.group && option.group !== lastGroup) {
      lastGroup = option.group;
      rows.push(
        <span className="pop-section" key={`g-${option.group}`}>
          {option.group}
        </span>
      );
    }
    rows.push(
      <OptionRow
        key={option.value}
        option={option}
        multi={multi}
        selected={selected.has(option.value)}
        cursor={index === cursor}
        onHover={() => setCursor(index)}
        onPick={() => onPick(option.value)}
      />
    );
  });

  return (
    <div
      className="pop-options"
      ref={listRef}
      role="none"
      // Focusable so the arrow keys work with no search box above it. The
      // buttons inside are still reachable by Tab; this only gives the list
      // somewhere to receive a key press.
      tabIndex={onKeyDown ? -1 : undefined}
      onKeyDown={onKeyDown}
      data-autofocus={autoFocus ? "" : undefined}
    >
      {rows}
    </div>
  );
}

/**
 * A footer action opens something else — a "create and assign" dialog, most of
 * the time. The menu has to get out of the way first, or its listbox sits over
 * the dialog it just opened and swallows every click aimed at it.
 *
 * Wrapping rather than asking each caller to remember means a footer added
 * later cannot get this wrong. `display: contents` keeps the layout identical.
 */
function FooterAction({ close, children }: { close: () => void; children: ReactNode }) {
  return (
    <span style={{ display: "contents" }} onClick={close}>
      {children}
    </span>
  );
}

/* ── single select ─────────────────────────────────────────────────────── */

export function Select<T extends string>({
  value,
  options,
  onChange,
  label,
  placeholder = "Choose",
  searchable,
  clearable = false,
  disabled = false,
  compact = false,
  className = "",
  placement = "bottom-start",
  footer,
  onCreate,
  createLabel = (name: string) => `Add “${name}”`,
  emptyLabel = "Nothing matches that.",
  renderTrigger,
}: {
  value: T | null;
  options: Option<T>[];
  onChange: (value: T | null) => void;
  label: string;
  placeholder?: string;
  /** Defaults to on once there are more than eight options. */
  searchable?: boolean;
  clearable?: boolean;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
  placement?: Placement;
  footer?: ReactNode;
  /**
   * Offered when the search matches nothing: make one called that.
   *
   * A picker that answers "nothing matches" and stops is a dead end — the
   * thing being searched for does not exist yet, and the person searching is
   * the one who would create it.
   */
  onCreate?: (name: string) => void;
  createLabel?: (name: string) => string;
  emptyLabel?: string;
  renderTrigger?: (selected: Option<T> | null, open: boolean) => ReactNode;
}) {
  const { anchorRef, open, setOpen, close } = usePopover();
  const [query, setQuery] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const showSearch = searchable ?? options.length > 8;

  const matches = useMemo(() => filterOptions(options, query), [options, query]);
  const selected = options.find((option) => option.value === value) ?? null;

  const commit = useCallback(
    (next: T) => {
      onChange(next);
      close();
      setQuery("");
    },
    [onChange, close]
  );

  const { cursor, setCursor, onKeyDown } = useListCursor(matches.length, (index) => {
    const option = matches[index];
    if (option && !option.disabled) commit(option.value);
  });

  useEffect(() => {
    if (open && showSearch) input.current?.focus();
  }, [open, showSearch]);

  useEffect(() => {
    if (!open) return;
    const index = matches.findIndex((option) => option.value === value);
    setCursor(index >= 0 ? index : 0);
    // Only when the menu opens: re-running on every keystroke would fight the
    // cursor the person is moving.
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => setCursor(0), [query, setCursor]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        className={`combobox-trigger${compact ? " compact" : ""} ${className}`.trim()}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(!open)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {renderTrigger ? (
          renderTrigger(selected, open)
        ) : (
          <>
            {selected?.image !== undefined ? (
              <Avatar name={selected?.label ?? ""} src={selected?.image ?? null} size="small" square />
            ) : selected?.colour ? (
              <i style={{ width: 9, height: 9, borderRadius: 3, background: selected.colour, marginLeft: 5, flex: "none" }} />
            ) : selected?.icon ? (
              <span style={{ display: "grid", placeItems: "center", marginLeft: 5 }}>{selected.icon}</span>
            ) : (
              <span style={{ width: 5 }} />
            )}
            <span className={`combobox-value${selected ? "" : " placeholder"}`}>
              <strong>{selected?.label ?? placeholder}</strong>
              {selected?.hint ? <small>{selected.hint}</small> : null}
            </span>
          </>
        )}
        <ChevronIcon size={12} className="combobox-chevron" />
      </button>

      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={close}
        role="listbox"
        label={label}
        placement={placement}
        matchWidth
        minWidth={216}
      >
        {showSearch ? (
          <div className="pop-search">
            <label className="search-box">
              <SearchIcon size={13} />
              <input
                ref={input}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search…"
                aria-label={`Search ${label}`}
                data-autofocus
              />
              {query ? (
                <button type="button" className="text-button tiny" onClick={() => setQuery("")}>
                  clear
                </button>
              ) : null}
            </label>
          </div>
        ) : null}

        <OptionList
          options={matches}
          selected={new Set(value ? [value] : [])}
          multi={false}
          cursor={cursor}
          setCursor={setCursor}
          onPick={(next) => commit(next)}
          emptyLabel={emptyLabel}
          emptyAction={
            onCreate && query.trim() ? (
              <button
                type="button"
                className="button brass compact"
                onClick={() => {
                  const name = query.trim();
                  setQuery("");
                  close();
                  onCreate(name);
                }}
              >
                {createLabel(query.trim())}
              </button>
            ) : null
          }
          onKeyDown={onKeyDown}
          autoFocus={!showSearch}
        />

        {(clearable && value) || footer ? (
          <div className="pop-foot">
            {clearable && value ? (
              <button
                type="button"
                className="text-button tiny"
                onClick={() => {
                  onChange(null);
                  close();
                }}
              >
                Clear
              </button>
            ) : (
              <span>↑↓ to move · ↵ to choose</span>
            )}
            <FooterAction close={close}>{footer}</FooterAction>
          </div>
        ) : (
          <div className="pop-foot">
            <span>↑↓ to move · ↵ to choose</span>
            <b>{matches.length}</b>
          </div>
        )}
      </Popover>
    </>
  );
}

/* ── multi select ──────────────────────────────────────────────────────── */

export function MultiSelect<T extends string>({
  values,
  options,
  onChange,
  label,
  placeholder = "None",
  searchable,
  compact = false,
  disabled = false,
  className = "",
  placement = "bottom-start",
  emptyLabel = "Nothing matches that.",
  summary,
  footer,
}: {
  values: T[];
  options: Option<T>[];
  onChange: (values: T[]) => void;
  label: string;
  placeholder?: string;
  searchable?: boolean;
  compact?: boolean;
  disabled?: boolean;
  className?: string;
  placement?: Placement;
  emptyLabel?: string;
  summary?: (chosen: Option<T>[]) => ReactNode;
  footer?: ReactNode;
}) {
  const { anchorRef, open, setOpen, close } = usePopover();
  const [query, setQuery] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const showSearch = searchable ?? options.length > 8;

  const matches = useMemo(() => filterOptions(options, query), [options, query]);
  const chosen = useMemo(() => options.filter((option) => values.includes(option.value)), [options, values]);
  const selectedSet = useMemo(() => new Set<string>(values), [values]);

  const toggle = useCallback(
    (next: T) => {
      // The menu deliberately stays open. Assigning four tags should be four
      // clicks, not four rounds of reopening the same menu.
      onChange(values.includes(next) ? values.filter((value) => value !== next) : [...values, next]);
    },
    [values, onChange]
  );

  const { cursor, setCursor, onKeyDown } = useListCursor(matches.length, (index) => {
    const option = matches[index];
    if (option && !option.disabled) toggle(option.value);
  });

  useEffect(() => {
    if (open && showSearch) input.current?.focus();
  }, [open, showSearch]);
  useEffect(() => setCursor(0), [query, setCursor]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        className={`combobox-trigger${compact ? " compact" : ""} ${className}`.trim()}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(!open)}
      >
        <span style={{ width: 5 }} />
        <span className={`combobox-value${chosen.length ? "" : " placeholder"}`}>
          <strong>
            {summary
              ? summary(chosen)
              : chosen.length === 0
                ? placeholder
                : chosen.length <= 2
                  ? chosen.map((option) => option.label).join(", ")
                  : `${chosen.length} selected`}
          </strong>
        </span>
        <ChevronIcon size={12} className="combobox-chevron" />
      </button>

      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={close}
        role="listbox"
        label={label}
        placement={placement}
        matchWidth
        minWidth={224}
      >
        {showSearch ? (
          <div className="pop-search">
            <label className="search-box">
              <SearchIcon size={13} />
              <input
                ref={input}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search…"
                aria-label={`Search ${label}`}
                data-autofocus
              />
            </label>
          </div>
        ) : null}

        <OptionList
          options={matches}
          selected={selectedSet}
          multi
          cursor={cursor}
          setCursor={setCursor}
          onPick={(next) => toggle(next)}
          emptyLabel={emptyLabel}
          onKeyDown={onKeyDown}
          autoFocus={!showSearch}
        />

        <div className="pop-foot">
          <button type="button" className="text-button tiny" onClick={() => onChange([])} disabled={values.length === 0}>
            Clear all
          </button>
          {footer ? <FooterAction close={close}>{footer}</FooterAction> : <b>{values.length} of {options.length}</b>}
        </div>
      </Popover>
    </>
  );
}

/* ── the creator picker ────────────────────────────────────────────────── */

/**
 * The one from numero-creators, with the trigger widened so it reads as the
 * subject of the page rather than a filter.
 */
export function CreatorCombobox({
  creators,
  value,
  onChange,
  allowNone = true,
  label = "Creator",
  compact = false,
  className = "",
}: {
  creators: { id: string; name: string; avatar?: string | null; status?: string; linkCount?: number }[];
  value: string | null;
  onChange: (id: string | null) => void;
  allowNone?: boolean;
  label?: string;
  compact?: boolean;
  className?: string;
}) {
  const options = useMemo<Option<string>[]>(() => {
    const live = creators
      .filter((creator) => creator.status !== "archived")
      .map((creator) => ({
        value: creator.id,
        label: creator.name,
        image: creator.avatar ?? null,
        hint:
          creator.linkCount === undefined
            ? undefined
            : `${creator.linkCount} link${creator.linkCount === 1 ? "" : "s"}`,
      }));
    const archived = creators
      .filter((creator) => creator.status === "archived")
      .map((creator) => ({
        value: creator.id,
        label: creator.name,
        image: creator.avatar ?? null,
        hint: "archived",
        group: "Archived",
      }));
    return [...live, ...archived];
  }, [creators]);

  return (
    <Select
      value={value}
      options={options}
      onChange={onChange}
      label={label}
      placeholder="Choose a creator"
      searchable
      clearable={allowNone}
      compact={compact}
      className={className}
      emptyLabel="Nobody matches that."
    />
  );
}
